import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { authAPI } from '../../../utils/api';
import AccountSettingsModal from '../../../components/AccountSettingsModal';

const RetailerProfile = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { showSuccess, showError } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    setProfileData({
      ...profileData,
      [e.target.name]: e.target.value
    });
  };

  const updateProfile = async () => {
    try {
      const res = await authAPI.updateProfile(profileData);
      if (res.data?.success) {
        showSuccess('Profile updated successfully');
        setIsEditing(false);
        window.location.reload();
      } else {
        showError(res.data?.message || 'Failed to update profile');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gold">My Profile</h1>
        <button
          onClick={() => setIsEditing(!isEditing)}
          className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
        >
          {isEditing ? 'Cancel' : 'Edit'}
        </button>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <div className="flex items-center mb-6">
          <div className="w-20 h-20 bg-gold rounded-full flex items-center justify-center mr-4">
            <span className="text-2xl font-bold text-dark">
              {user?.name?.charAt(0).toUpperCase() || 'R'}
            </span>
          </div>
          <div>
            <h2 className="text-xl font-semibold">{user?.name || 'Retailer'}</h2>
            <p className="text-gray-400 capitalize">{user?.role || 'retailer'}</p>
            {user?.is_approved && (
              <span className="bg-green-900 text-green-300 px-2 py-1 rounded-full text-xs mt-1 inline-block">
                Verified
              </span>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-400 mb-2">Business Name</label>
            {isEditing ? (
              <input
                type="text"
                name="name"
                value={profileData.name}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.name}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Email</label>
            <p className="text-light">{profileData.email}</p>
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                name="phone"
                value={profileData.phone}
                onChange={handleInputChange}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.phone || 'Not provided'}</p>
            )}
          </div>

          <div>
            <label className="block text-gray-400 mb-2">Address</label>
            {isEditing ? (
              <textarea
                name="address"
                value={profileData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-light focus:outline-none focus:ring-2 focus:ring-gold"
              />
            ) : (
              <p className="text-light">{profileData.address || 'Not provided'}</p>
            )}
          </div>
        </div>

        {isEditing && (
          <button
            onClick={updateProfile}
            className="w-full bg-gold text-dark py-3 rounded-lg font-medium mt-6"
          >
            Save Changes
          </button>
        )}
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Support & Legal</h3>
        <div className="space-y-3">
          <button
            onClick={() => navigate('/terms-and-conditions')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Terms & Conditions
          </button>
          <button
            onClick={() => navigate('/privacy-policy')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate('/about-us')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
          >
            About Us
          </button>
          <button
            onClick={() => navigate('/patient/support')}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex justify-between items-center"
          >
            <span>Contact Support</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Account Settings</h3>
        <div className="space-y-3">
          <button
            onClick={() => setShowSettingsModal(true)}
            className="w-full text-left py-3 px-4 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors flex justify-between items-center"
          >
            <span>Change Password / Delete Account</span>
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          <button
            onClick={handleLogout}
            className="w-full text-left py-3 px-4 bg-red-900 text-red-300 rounded-lg hover:bg-red-800 transition-colors"
          >
            Logout
          </button>
        </div>
      </div>

      <AccountSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
      />

    </div>
  );
};

export default RetailerProfile;
